/**
 * ai-agent.actions.ts — NgRx actions for the generic AI agent layer.
 */
import { createAction, props } from '@ngrx/store';

import { AiAgentJobRequest } from '../interfaces/ai-agent-job-request.interface';
import { AiAgentSession } from '../interfaces/ai-agent-session.interface';
import {
    AiJobProgressEvent,
    AiJobCompletedEvent,
} from '../interfaces/ai-agent-progress-event.interface';
import { AiBackendError } from '../interfaces/ai-backend-error';

// ── Job Lifecycle ──────────────────────────────────────────────────────

export const submitJob = createAction(
    '[AI Agent] Submit Job',
    props<{ request: AiAgentJobRequest }>(),
);

export const submitJobSuccess = createAction(
    '[AI Agent] Submit Job Success',
    props<{ sessionId: string }>(),
);

export const submitJobFailed = createAction(
    '[AI Agent] Submit Job Failed',
    props<{ error: AiBackendError }>(),
);

// ── WebSocket Events (dispatched by effects when WS events arrive) ────

export const jobProgressReceived = createAction(
    '[AI Agent] Job Progress',
    props<{ event: AiJobProgressEvent }>(),
);

export const jobCompletedReceived = createAction(
    '[AI Agent] Job Completed',
    props<{ event: AiJobCompletedEvent }>(),
);

// ── Session Polling (fallback if WS disconnects) ──────────────────────

export const pollSession = createAction(
    '[AI Agent] Poll Session',
    props<{ sessionId: string }>(),
);

export const pollSessionSuccess = createAction(
    '[AI Agent] Poll Session Success',
    props<{ session: AiAgentSession }>(),
);

export const pollSessionFailed = createAction(
    '[AI Agent] Poll Session Failed',
    props<{ error: AiBackendError }>(),
);

// ── Chat ──────────────────────────────────────────────────────────────

export const sendChat = createAction(
    '[AI Agent] Send Chat',
    props<{ sessionId: string; message: string; sectionKey?: string }>(),
);

export const sendChatSuccess = createAction(
    '[AI Agent] Send Chat Success',
    props<{
        sessionId: string;
        reply: string;
        replyKey?: string;
        replyParams?: Record<string, unknown>;
        output?: Record<string, unknown> | null;
    }>(),
);

export const sendChatFailed = createAction(
    '[AI Agent] Send Chat Failed',
    props<{ error: AiBackendError }>(),
);

// ── Socket Connection ─────────────────────────────────────────────────

export const connectSocket = createAction(
    '[AI Agent] Connect Socket',
    props<{ token: string }>(),
);

export const disconnectSocket = createAction(
    '[AI Agent] Disconnect Socket',
);

/** Dispatched by the effect that mirrors AiAgentSocketService.isConnected$ into the store */
export const socketConnectedChanged = createAction(
    '[AI Agent] Socket Connected Changed',
    props<{ connected: boolean }>(),
);

// ── Reset ─────────────────────────────────────────────────────────────

export const resetSession = createAction(
    '[AI Agent] Reset Session',
);
